import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import { formatNOK } from "@/lib/format";
import {
  useVoucherAllocations,
  useSaveVoucherAllocations,
  useSuggestAllocations,
} from "@/hooks/use-voucher-allocations";
import {
  useVoucherAccountMappings,
  useUpsertVoucherAccountMappings,
} from "@/hooks/use-voucher-account-mappings";
import {
  VoucherAllocationRow,
  type AllocationRowState,
  type BudgetLineOption,
} from "./VoucherAllocationRow";
import { VoucherDocumentPreview } from "./VoucherDocumentPreview";

interface Props {
  voucherId: string;
  voucherTotal: number;
  projectId: string;
  budgetLines: BudgetLineOption[];
  /** VIB-1033: tittel på dokument-previewen (leverandør / bilagsnr). */
  documentTitle: string;
  onClose: () => void;
}

const TOLERANCE = 0.01;

/**
 * VIB-997: Fordelings-editor som lar PL koble hver bilagspost (forhåndsfylt
 * fra PO via voucher_allocations) til en budsjettlinje. Forhåndsvalg via
 * konto→mal-cache (vinner) eller LLM-forslag. «Lagre» er gatet på at summen
 * matcher bilagstotal. Poster trenger IKKE en budsjettlinje — kostnader som
 * ikke tilfaller noen linje (f.eks. reisekostnader, feedback 29.06) kan lagres
 * som «uknyttet» (budget_line_id = null). De påvirker ikke per-linje-avvik, men
 * teller fortsatt i bilagstotalen og i BF (som synkes fra PowerOffice).
 */
export function VoucherAllocationEditor({
  voucherId,
  voucherTotal,
  projectId,
  budgetLines,
  documentTitle,
  onClose,
}: Props) {
  const { data: allocations, isLoading } = useVoucherAllocations(voucherId);
  const {
    data: accountMappings,
    isSuccess: mappingsSettledOk,
    isError: mappingsFailed,
  } = useVoucherAccountMappings();
  const suggest = useSuggestAllocations();
  const save = useSaveVoucherAllocations();
  const upsertMappings = useUpsertVoucherAccountMappings();

  const [rows, setRows] = useState<AllocationRowState[]>([]);
  const hydrated = useRef(false);

  // Kopier allokeringene inn i lokal state én gang når begge spørringene er
  // klare. Vi MÅ vente på account-mappings (success ELLER error) før vi
  // hydrerer, ellers kan konto→mal-cachen være undefined når allocations
  // resolver først, og da faller alle rader til LLM (race, VIB-997-review).
  useEffect(() => {
    if (hydrated.current) return;
    if (!allocations) return;
    // Account-mappings-spørringen må ha settlet før vi låser hydreringen.
    const mappingsReady = mappingsSettledOk || mappingsFailed;
    if (!mappingsReady) return;
    hydrated.current = true;

    const initial: AllocationRowState[] = allocations.map((a) => ({
      budget_line_id: a.budget_line_id,
      amount: a.amount,
      description: a.description,
      po_account_id: a.po_account_id,
      source: a.source,
    }));

    // 1) Cache-treff: konto→mal → entydig budsjettlinje med matchende template_id.
    const withCache = initial.map((r) => {
      if (r.budget_line_id || r.po_account_id == null || !accountMappings) {
        return r;
      }
      const templateId = accountMappings.get(r.po_account_id);
      if (!templateId) return r;
      const matches = budgetLines.filter((b) => b.template_id === templateId);
      return matches.length === 1
        ? { ...r, budget_line_id: matches[0].id }
        : r;
    });
    setRows(withCache);

    // 2) LLM-forslag for rader uten cache-treff. Cache-treff vinner.
    const needsSuggestion = withCache.some((r) => !r.budget_line_id);
    if (needsSuggestion && budgetLines.length > 0) {
      suggest
        .mutateAsync({
          posts: withCache.map((r) => ({
            description: r.description,
            amount: r.amount,
            po_account_id: r.po_account_id,
          })),
          budgetLines: budgetLines.map((b) => ({
            id: b.id,
            description: b.description,
            phase: b.phase,
            cost_type: b.cost_type,
          })),
        })
        .then((suggestions) => {
          if (suggestions.length === 0) return;
          setRows((prev) =>
            prev.map((r, i) => {
              if (r.budget_line_id) return r;
              const s = suggestions.find((x) => x.index === i);
              // LLM kan returnere en id som ikke finnes i budsjettlinjene (eller
              // null). Bruk kun forslaget hvis det matcher en faktisk linje.
              if (s?.budget_line_id && budgetLines.some((b) => b.id === s.budget_line_id)) {
                return { ...r, budget_line_id: s.budget_line_id };
              }
              return r;
            }),
          );
        });
    }
    // Vi kjører kun ved første hydrering (gatet av hydrated-ref). Deps trigger
    // re-evaluering når enten allocations eller mappings-spørringen settler,
    // slik at cache-forhåndsvalget blir rekkefølge-uavhengig. suggest/budgetLines
    // leses inne i effekten men er stabile nok for engangs-hydrering.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocations, mappingsSettledOk, mappingsFailed]);

  const patchRow = (idx: number, patch: Partial<AllocationRowState>) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const removeRow = (idx: number) =>
    setRows((prev) => prev.filter((_, i) => i !== idx));

  const addRow = () =>
    setRows((prev) => [
      ...prev,
      {
        budget_line_id: null,
        amount: 0,
        description: null,
        po_account_id: null,
        source: "manual",
      },
    ]);

  const sum = useMemo(
    () => rows.reduce((s, r) => s + (r.amount || 0), 0),
    [rows],
  );
  const sumMatches = Math.abs(sum - voucherTotal) <= TOLERANCE;
  // Uknyttede poster (uten budsjettlinje) er tillatt (feedback 29.06): kostnader
  // som ikke tilfaller noen linje skal kunne lagres. «Lagre» krever kun at
  // summen matcher bilagstotalen, så hele bilaget er gjort rede for.
  const unassignedCount = rows.filter((r) => !r.budget_line_id).length;
  const canSave = rows.length > 0 && sumMatches && !save.isPending;

  const handleSave = async () => {
    try {
      await save.mutateAsync({
        voucherId,
        projectId,
        allocations: rows.map((r) => ({
          budget_line_id: r.budget_line_id,
          amount: r.amount,
          description: r.description,
          po_account_id: r.po_account_id,
          source: r.source,
        })),
      });

      // Lær konto→mal for rader med konto + linje som har template_id.
      const learn = rows
        .map((r) => {
          if (r.po_account_id == null || !r.budget_line_id) return null;
          const bl = budgetLines.find((b) => b.id === r.budget_line_id);
          if (!bl?.template_id) return null;
          return { po_account_id: r.po_account_id, template_id: bl.template_id };
        })
        .filter((x): x is { po_account_id: number; template_id: string } => !!x);
      if (learn.length > 0) await upsertMappings.mutateAsync(learn);

      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? `Kunne ikke lagre fordelingen: ${err.message}`
          : "Kunne ikke lagre fordelingen.",
      );
    }
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* Venstre: dokument-preview (VIB-1033). */}
      <VoucherDocumentPreview voucherId={voucherId} title={documentTitle} />

      {/* Høyre: fordelings-UI. */}
      <div className="space-y-3">
      {isLoading ? (
        <p className="text-sm text-muted-foreground py-4">Laster poster...</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">
          Ingen poster. Legg til en for å fordele bilaget.
        </p>
      ) : (
        <div className="divide-y rounded-md border px-3">
          {rows.map((r, i) => (
            <VoucherAllocationRow
              key={i}
              row={r}
              budgetLines={budgetLines}
              onChange={(patch) => patchRow(i, patch)}
              onRemove={() => removeRow(i)}
            />
          ))}
        </div>
      )}

      <Button type="button" variant="outline" size="sm" onClick={addRow}>
        <SrIcon name="plus" className="w-4 h-4 mr-1" />
        Legg til post
      </Button>

      {unassignedCount > 0 && (
        <p className="text-xs text-muted-foreground">
          {unassignedCount === 1
            ? "1 post er ikke knyttet til en budsjettlinje og lagres som uknyttet."
            : `${unassignedCount} poster er ikke knyttet til en budsjettlinje og lagres som uknyttet.`}
        </p>
      )}

      <div className="flex items-center justify-between text-sm border-t pt-3">
        <div
          className={cn(
            "flex items-center gap-1.5 font-medium",
            sumMatches ? "text-success" : "text-destructive",
          )}
        >
          {sumMatches && <SrIcon name="check" className="w-4 h-4" />}
          <span>
            {formatNOK(sum)} / {formatNOK(voucherTotal)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Avbryt
          </Button>
          <Button type="button" size="sm" disabled={!canSave} onClick={handleSave}>
            {save.isPending ? "Lagrer..." : "Lagre"}
          </Button>
        </div>
      </div>
      </div>
    </div>
  );
}
